import {
  BufferAttribute,
  InstancedBufferGeometry,
  InstancedInterleavedBuffer,
  InterleavedBufferAttribute,
  Mesh,
  type Material,
  OrthographicCamera,
  Scene,
  type Texture,
} from "three/webgpu";
import type { GpuContext } from "./GpuContext.js";

const INITIAL_CAPACITY = 256;

/**
 * One run of instances that sample the same texture: its own instance buffer and mesh, drawn in order.
 *
 * three draws every instance of a geometry from the first, so runs cannot share one buffer the way a
 * hand-written renderer would with a first-instance offset. Each owns its array instead, and the pool keeps
 * them from frame to frame.
 */
interface Run {
  texture: Texture;
  data: Float32Array;
  capacity: number;
  count: number;
  buffer: InstancedInterleavedBuffer;
  geometry: InstancedBufferGeometry;
  mesh: Mesh;
}

/** The unit quad every instance expands: four corners in 0..1, two triangles. */
const QUAD_POSITION = new Float32Array([0, 0, 0, 1, 0, 0, 0, 1, 0, 1, 1, 0]);
const QUAD_INDEX = new Uint16Array([0, 1, 2, 2, 1, 3]);

/**
 * Instanced quads, packed by the caller and drawn in submission order in as few draws as the textures
 * allow: a new run starts only when the texture changes. What the UI's quad batcher and engine2d's sprite
 * batch both are underneath.
 *
 * Each instance is `stride` floats, exposed to the material as vec4 attributes named by `attributes`, in
 * order. The quad's corner is the `position` attribute, (0,0) to (1,1).
 */
export class InstanceRuns {
  readonly stride: number;
  #gpu: GpuContext;
  #attributes: readonly string[];
  #materialFor: (texture: Texture) => Material;
  #runs: Run[] = [];
  #runCount = 0;
  #current: Run | null = null;
  #count = 0;
  #scene = new Scene();
  #camera = new OrthographicCamera();
  #position = new BufferAttribute(QUAD_POSITION, 3);
  #index = new BufferAttribute(QUAD_INDEX, 1);
  /** Reusable result of `reserve`, so packing allocates nothing. */
  #slot: { data: Float32Array; index: number } = { data: new Float32Array(0), index: 0 };

  constructor(gpu: GpuContext, attributes: readonly string[], materialFor: (texture: Texture) => Material) {
    this.#gpu = gpu;
    this.#attributes = attributes;
    this.stride = attributes.length * 4;
    this.#materialFor = materialFor;
  }

  get instanceCount(): number {
    return this.#count;
  }

  get drawCallCount(): number {
    return this.#runCount;
  }

  /** The texture of the run instances are going into, or null before the first. */
  get currentTexture(): Texture | null {
    return this.#current?.texture ?? null;
  }

  reset(): void {
    this.#count = 0;
    this.#runCount = 0;
    this.#current = null;
  }

  /**
   * Claim the next instance slot in a run sampling `texture`, starting a run if the open one samples
   * another. Returns the array and the float index the instance starts at; valid until the next call.
   */
  reserve(texture: Texture): { data: Float32Array; index: number } {
    let run = this.#current;
    if (!run || run.texture !== texture) run = this.#open(texture);
    if (run.count === run.capacity) this.#grow(run);
    this.#slot.data = run.data;
    this.#slot.index = run.count * this.stride;
    run.count++;
    this.#count++;
    return this.#slot;
  }

  #open(texture: Texture): Run {
    let run = this.#runs[this.#runCount];
    if (!run) {
      run = this.#make(texture, INITIAL_CAPACITY);
      this.#runs.push(run);
    }
    run.texture = texture;
    run.count = 0;
    this.#runCount++;
    this.#current = run;
    return run;
  }

  #make(texture: Texture, capacity: number): Run {
    const data = new Float32Array(capacity * this.stride);
    const buffer = new InstancedInterleavedBuffer(data, this.stride, 1);
    const geometry = new InstancedBufferGeometry();
    geometry.setAttribute("position", this.#position);
    geometry.setIndex(this.#index);
    this.#attributes.forEach((name, k) => geometry.setAttribute(name, new InterleavedBufferAttribute(buffer, 4, k * 4)));
    geometry.instanceCount = 0;
    const mesh = new Mesh(geometry, this.#materialFor(texture));
    mesh.frustumCulled = false;
    mesh.matrixAutoUpdate = false;
    return { texture, data, capacity, count: 0, buffer, geometry, mesh };
  }

  /** Double a run's capacity. A buffer's size is fixed once uploaded, so the geometry is rebuilt around a new one. */
  #grow(run: Run): void {
    const bigger = this.#make(run.texture, run.capacity * 2);
    bigger.data.set(run.data.subarray(0, run.count * this.stride));
    bigger.count = run.count;
    run.geometry.dispose();
    Object.assign(run, bigger);
  }

  /** Upload the frame's instances and draw them into whatever the renderer is pointed at. */
  flush(): void {
    const scene = this.#scene;
    scene.clear();
    for (let n = 0; n < this.#runCount; n++) {
      const run = this.#runs[n]!;
      if (run.count === 0) continue;
      run.mesh.material = this.#materialFor(run.texture);
      run.geometry.instanceCount = run.count;
      run.buffer.clearUpdateRanges();
      run.buffer.addUpdateRange(0, run.count * this.stride);
      run.buffer.needsUpdate = true;
      run.mesh.renderOrder = n;
      scene.add(run.mesh);
    }
    if (scene.children.length > 0) this.#gpu.renderer.render(scene, this.#camera);
  }

  dispose(): void {
    for (const run of this.#runs) run.geometry.dispose();
    this.#runs.length = 0;
  }
}
